import React from "react";
import { useEffect, useState } from "react";

import { Table, InputPicker, TagGroup, Tag } from "rsuite";
import Button from "rsuite/Button";
import EditIcon from "@rsuite/icons/Edit";
import FunnelIcon from "@rsuite/icons/Funnel";

import ConfigurationPage from "templates/ConfigurationPage";
import TableWithSearch from "components/TableWithSearch";
import FilterSidePanel from "./FilterSidePanel";
import UsersTableEmptyMessage from "./UsersTableEmptyMessage";
import useRequestHandler from "hooks/useRequestHandler";
import { uniqueValuesArray } from "./UserContent/partial/util";

const moment = require("moment-timezone");
const { Column, HeaderCell, Cell } = Table;

const UserList = ({ history, auth }) => {

    const [userListLoading, setUserListLoading] = useState(false);
    const [filterPanelVisibility, setFilterPanelVisibility] = useState(false);
    const [userList, setUserList] = useState([]);
    const [filteredUserData, setFilteredUserData] = useState([]);
    const [searchedData, setSearchedData] = useState([]);

    const [locationsList, setLocationsList] = useState([]);
    const [sectionsList, setSectionsList] = useState([]);
    const [roomsList, setRoomsList] = useState([]);

    const [filterData, setFilterData] = useState({
        facility: null,
        section: null,
        room_name: null
    });

    const [applyFilter, setApplyFilter] = useState(false);

    useEffect(() => {
        getUsers();
    }, [])

    const getUsersRequest = useRequestHandler({
        auth,
        apiArguments: [{ orgId: auth.currentUser.defaultOrg }],
        apiName: "getUsers",
        successMessageData: { show: false },
        errorMessageData: {
            title: "Warning",
            message: "Unable to load users, just add a new one.",
            type: "info"
        }
    })

    const getUsers = async () => {
        setUserListLoading(true);
        await getUsersRequest(data => {
            const newData = data.map((user) => ({ ...user, dob: moment(user.dob).format("l") }))
            setUserList(newData);
            setFilteredUserData(newData);
        });

        setUserListLoading(false);
    }

    const addNewUser = () => {
        history.push(`/configuration/users/new-user/`);
    }

    const goToEditUserPage = ({ id }, { target }) => {
        if (target.ariaLabel === "edit" || (target.nearestViewportElement?.ariaLabel?.includes("edit"))) {
            history.push(`/configuration/users/${id}/edit`);q
        }
    }

    const closeFilterPanel = () => setFilterPanelVisibility(false);


    const addUser = () => {
        return <Button onClick={addNewUser} color="cyan" appearance="primary" size="lg">
            New User
        </Button>
    }

    const onOpenHandler = () => {
        setLocationsList(uniqueValuesArray(availableList(), "facility"));
        setAvailableSection();
        setAvailableRoom();
    }

    const availableList = (list) => searchedData?.length ? searchedData: list ||userList;

    const setAvailableSection = () => {
        if(filterData.facility) {
            const patientsByLocation = (availableList()).filter(({ facility }) => facility === filterData.facility);
            setSectionsList(uniqueValuesArray(patientsByLocation, "section"));
        } else {
            setSectionsList(uniqueValuesArray(availableList(), "section"));  
        }
    }

    const setAvailableRoom = () => {
        if(filterData.section) {
            setRoomsList(uniqueValuesArray(availableList(filteredUserData), "room_name"));
        } else {
            setRoomsList(uniqueValuesArray(availableList(), "room_name"));
        }
    }

    const addFilterLayout = () => (
        <Button disabled={filterPanelVisibility} appearance="ghost" color="cyan" className={"filter-icon-btn"}>
            <FunnelIcon color="cyan" style={{ fontSize: "2em" }} onClick={() => setFilterPanelVisibility(true)} />
        </Button>);

    const changeLocationHandler = (value) => {
        if (value) {
            setFilterData({ ...filterData, facility: value });
            const patientsByLocation = (availableList()).filter(({ facility }) => facility === value);
            setSectionsList(uniqueValuesArray(patientsByLocation, "section"));
            filterData.section || setRoomsList(uniqueValuesArray(patientsByLocation, "room_name"));
        } else {
           setFilterData({ ...filterData, facility: null });
           setSectionsList(uniqueValuesArray(availableList(), "section"));
           setAvailableRoom();
        }
    }

    const changeSectionHandler = (value) => {
        if (value) {
            setFilterData({ ...filterData, section: value });
            const patientsBySection = (availableList()).filter(({ section }) => section === value);
            setLocationsList(uniqueValuesArray(patientsBySection, "facility"));
            setRoomsList(uniqueValuesArray(patientsBySection, "room_name"));
        } else {
            setFilterData({ ...filterData, section: null });
            setAvailableRoom();
        }
    }

    const changeRoomHandler = (value) => {
        if (value) {
            setFilterData({ ...filterData, room_name: value });
            const patientsByRoom = (availableList()).filter(({ room_name }) => room_name === value);
            setLocationsList(uniqueValuesArray(patientsByRoom, "facility"));
            setSectionsList(uniqueValuesArray(patientsByRoom, "section"));
        } else {
            setFilterData({ ...filterData, room_name: null });
            setAvailableSection();
        }
    }

    const handlerFilterChange = (name, value) => {
        if (name === "location") changeLocationHandler(value);
        if (name === "section") changeSectionHandler(value);
        if (name === "room") changeRoomHandler(value);
    }

    const applyFilterHandler = () => {
        closeFilterPanel();
        filterHandler(filterData);
    }

    const clearFiltersHandler = () => {
        filterPanelVisibility && closeFilterPanel();
        setFilteredUserData(userList);
        setApplyFilter(false);
        setFilterData({});
    }

    const filterHandler = (data) => {
        const dataFilterExist = Object.values(data).filter(item => item);

        if (!!dataFilterExist.length) {
            setApplyFilter(true);
            let filteredPatients = [];

            for (const key in data) {
                filteredPatients = data[key] ? userList.filter(item => item[key] === data[key]) : filteredPatients;
            }

            setFilteredUserData(filteredPatients);
        } else {
            setFilteredUserData(userList);
            setApplyFilter(false);
        }
    }

    const removeTag = tag => {
        let newFiltersData = {};

        for (const key in filterData) {
            if (filterData[key] == tag) newFiltersData = { ...filterData, [key]: null };
        }

        setFilterData(newFiltersData);
        filterHandler(newFiltersData);
    };

    const filtersData = () => (<div className={`list-filters-data`}>
        <div>Filters: </div>
        <TagGroup>
            {Object.values(filterData).filter(item => item).map((item, index) => (
                <Tag key={index} closable onClose={() => removeTag(item)}>
                    {item}
                </Tag>
            ))}
            <Tag closable onClose={clearFiltersHandler} className={"clear-filter"}>
                Clear All Filters
            </Tag>
        </TagGroup>
    </div>)

    return (
        <ConfigurationPage
            pageTitle="Users"
            subnavRight={addUser()}
            history={history}
        >
            <ConfigurationPage.Content>
                <FilterSidePanel
                    panelOpened={filterPanelVisibility}
                    onCloseHandler={closeFilterPanel}
                    clearFiltersHandler={clearFiltersHandler}
                    onOpenHandler={onOpenHandler}
                    auth={auth}
                    applyFilterHandler={applyFilterHandler}
                >
                    <div className="filter-wrapper">
                        <div className="filter">
                            <label>Location</label>
                            <InputPicker
                                data={locationsList}
                                className="filter-group"
                                placeholder={"Select location"}
                                onChange={(value) => handlerFilterChange("location", value)}
                                value={filterData.facility}
                            />
                        </div>

                        <div className="filter">
                            <label>Section</label>
                            <InputPicker
                                data={sectionsList}
                                className="filter-group"
                                placeholder={"Select section"}
                                onChange={(value) => handlerFilterChange("section", value)}
                                value={filterData.section}
                            />
                        </div>

                        <div className="filter">
                            <label>Room</label>
                            <InputPicker
                                data={roomsList}
                                className="filter-group"
                                placeholder={"Select room"}
                                onChange={(value) => handlerFilterChange("room", value)}
                                value={filterData.room_name}
                            />
                        </div>
                    </div>
                </FilterSidePanel>

                <TableWithSearch
                    data={filteredUserData}
                    dataLoading={userListLoading}
                    setData={setFilteredUserData}
                    searchData ={searchedData}
                    setSearchData={setSearchedData}
                    searchInputPlaceholder="Search patients by Last Name"
                    rightContent={addFilterLayout}
                    searchBy="lastName"
                    applyFilter={applyFilter}
                    filtersData={filtersData}
                    locale={UsersTableEmptyMessage}
                    onRowClick={goToEditUserPage}
                >
                    <Column flexGrow={4} sortable>
                        <HeaderCell><span className="header-cell">E-mail</span></HeaderCell>
                        <Cell dataKey="email" />
                    </Column>
                    <Column flexGrow={4} sortable>
                        <HeaderCell><span className="header-cell">Role</span></HeaderCell>
                        <Cell dataKey="role" />
                    </Column>
                    <Column flexGrow={4}>
                        <HeaderCell><span className="header-cell">Assigned Locations</span></HeaderCell>
                        <Cell dataKey="facilities" />
                    </Column>

                    <Column flexGrow={2} align="right">
                        <HeaderCell><span className="header-cell"></span></HeaderCell>
                        <Cell >
                            <EditIcon color="cyan" className="edit" />
                        </Cell>
                    </Column>
                </TableWithSearch>
            </ConfigurationPage.Content>
        </ConfigurationPage>
    );
};

export default UserList;